import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Alert,
    useMediaQuery,
    useTheme,
    Grid,
    Paper,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    Slide
} from '@mui/material';
import { DirectionsCar as CarIcon, Visibility, VisibilityOff, PersonOutline, LockOutlined } from '@mui/icons-material';
import { useStore } from '../stores';
import AuthLayout from '../components/layout/AuthLayout';

const Login: React.FC = () => {

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formErrors, setFormErrors] = useState<{ username?: string, password?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);


    const { authStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    useEffect(() => {
        const savedUsername = localStorage.getItem('rememberedUsername');
        if (savedUsername) {
            setUsername(savedUsername);
            setRememberMe(true);
        }
    }, []);


    useEffect(() => {

        if (authStore.isAuthenticated) {
            const from = (location.state as any)?.from?.pathname || '/dashboard';
            navigate(from, { replace: true });
        }
    }, [authStore.isAuthenticated, navigate, location]);


    const validateForm = (): boolean => {
        const errors: { username?: string, password?: string } = {};
        let isValid = true;

        if (!username.trim()) {
            errors.username = 'El nombre de usuario es obligatorio';
            isValid = false;
        }

        if (!password) {
            errors.password = 'La contraseña es obligatoria';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) return;
        if (!validateForm()) return;

        try {
            setIsSubmitting(true);

            if (rememberMe) {
                localStorage.setItem('rememberedUsername', username);
            } else {
                localStorage.removeItem('rememberedUsername');
            }

            const success = await authStore.login(username, password);

            if (success) {
                setLoginAttempts(0);
                const defaultRoute = '/dashboard';
                const from = (location.state as any)?.from?.pathname || defaultRoute;

                navigate(from, { replace: true });
            } else {
                setLoginAttempts(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error durante el login:', error);
        } finally {
            setIsSubmitting(false);
        }
    };


    const isTemporarilyLocked = loginAttempts >= 5;


    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isTemporarilyLocked) {
            timer = setTimeout(() => {
                setLoginAttempts(0);
            }, 30000);
        }
        return () => clearTimeout(timer);
    }, [isTemporarilyLocked]);

    return (
        <AuthLayout>
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Grid container spacing={0} sx={{ maxWidth: 1000, minWidth: '100%' }}>
                    {!isMobile && (
                        <Grid size={{ xs: 12, md: 6 }} sx={{ minHeight: '100vh' }}>
                            <Paper
                                elevation={3}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 4,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderRadius: 0,
                                }}
                            >
                                <CarIcon sx={{ fontSize: 80, mb: 4 }} />
                                <Typography variant="h4" gutterBottom>
                                    Sistema de Gestión de Flota
                                </Typography>
                                <Typography variant="body1" align="center">
                                    Monitoreo y control de vehículos en tiempo real
                                </Typography>
                            </Paper>
                        </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                borderRadius: 0,
                            }}
                        >
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                {isMobile && <CarIcon color="primary" sx={{ fontSize: 50, mb: 2 }} />}
                                <Typography variant="h5" component="h1" gutterBottom>
                                    {isMobile ? 'Sistema de Gestión de Flota' : 'Iniciar Sesión'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Ingrese sus credenciales para acceder al sistema
                                </Typography>
                            </Box>

                            {isTemporarilyLocked && (
                                <Slide direction="down" in={isTemporarilyLocked} mountOnEnter unmountOnExit>
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        Demasiados intentos fallidos. Por favor, intente nuevamente en 30 segundos.
                                    </Alert>
                                </Slide>
                            )}

                            {authStore.error && !isTemporarilyLocked && (
                                <Slide direction="down" in={!!authStore.error} mountOnEnter unmountOnExit>
                                    <Alert severity="error" sx={{ mb: 3 }}>
                                        {authStore.error}
                                    </Alert>
                                </Slide>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    label="Usuario"
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    autoFocus
                                    disabled={isSubmitting || isTemporarilyLocked}
                                    error={!!formErrors.username}
                                    helperText={formErrors.username}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonOutline color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    label="Contraseña"
                                    type={showPassword ? 'text' : 'password'}
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={isSubmitting || isTemporarilyLocked}
                                    error={!!formErrors.password}
                                    helperText={formErrors.password}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutlined color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={rememberMe}
                                            onChange={(e) => setRememberMe(e.target.checked)}
                                            color="primary"
                                            disabled={isSubmitting || isTemporarilyLocked}
                                        />
                                    }
                                    label="Recordar usuario"
                                    sx={{ mt: 1 }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    fullWidth
                                    sx={{ mt: 3, py: 1.2 }}
                                    disabled={isSubmitting || isTemporarilyLocked}
                                >
                                    {authStore.loading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Iniciar Sesión'
                                    )}
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </AuthLayout>
    );
};

export default observer(Login);